import {useSelector} from 'react-redux';

import {useLazyGetAutoFillTemplateQuery} from '@dms/api/core';
import {handleApiError} from '@dms/shared';

import {useBoolean} from 'shared/web';

import {useThunkDispatch} from '../../../hooks/useThunkDispatch';
import {updateAudit} from '../../../store/carAudit/actions';
import {AuditData, getAuditDataFromAutoFillTemplate} from '../../../store/carAudit/reducerUtils';
import {selectCategoriesIdsToOverrideByTemplate} from '../../../store/carAudit/selectors';
import {getUpdateRequestBodyFromFormData} from '../../../utils/getUpdateRequestBodyFromFormData';
import {useConditionContext} from './useConditionContext';

/**
 * Hook for applying auto fill template to audit.
 * It overrides only specific categories:
 *  - MECHANICS category and all its descendants
 *  - OVERALL_COMMENT category
 *  - all (audit) ratings by category id
 * When the template has no data for some category, the category is cleared (set to empty string `''`).
 */
export function useApplyAutoFillTemplate() {
  const thunkDispatch = useThunkDispatch();
  const {loadActualData} = useConditionContext();
  const [getAutoFillTemplateData] = useLazyGetAutoFillTemplateQuery();
  const [isLoading, startLoading, stopLoading] = useBoolean(false);
  const categoriesIdsToOverride = useSelector(selectCategoriesIdsToOverrideByTemplate);

  const applyAutoFillTemplate = async (templateId: string, audit: AuditData) => {
    startLoading();
    const autoFillTemplate = await getAutoFillTemplateData(
      {
        autoFillTemplateId: templateId,
        inspectionId: audit.id,
      },
      true
    )
      .unwrap()
      .catch(handleApiError);

    if (autoFillTemplate) {
      const mappedAuditFields = getAuditDataFromAutoFillTemplate(
        audit,
        autoFillTemplate,
        categoriesIdsToOverride
      );

      const updateAuditData = getUpdateRequestBodyFromFormData(mappedAuditFields);

      await thunkDispatch(
        updateAudit.action({
          auditId: audit.id,
          requestBody: updateAuditData,
        })
      )
        .unwrap()
        .catch(handleApiError);

      await loadActualData(audit.id).catch((error: {response: Error}) => {
        handleApiError(error.response);
      });
    }

    stopLoading();
  };

  return {applyAutoFillTemplate, isLoading};
}
